import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  AccessManagementDesignationRecord,
  AccessManagementDesignationType,
  AccessManagementOrgUnitCollectionOU,
  AccessManagementOrgUnitCollectionOURecord,
  AccessManagementOrgUnitCollectionRecord,
  AccessManagementPermissionAPIReturnFormats,
  AccessManagementPermissionMap,
  AccessManagementPermissionRecord,
  AccessManagementRolePermissionRecord,
  AccessManagementRoleRecord,
  AccessManagementUserCollectionRecord,
  AccessManagementUserCollectionUser,
  UserRecord,
} from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import {
  CdwConfirmationDialogComponent,
  CdwConfirmationDialogData,
  CdwDialogModule,
  ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import {
  CdwDrawerModule,
  DrawerNotificationService,
  openDrawer,
} from '@cdw-ae/common-ui-components-drawer';
import {
  CdwDrawerContentModule,
  DrawerContent,
} from '@cdw-ae/common-ui-components-drawer-content';
import { ApiResponse } from '@cdw-ae/common-ui-components-search-grid';
import { TreeNode } from '@cdw-ae/common-ui-components-tree';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { Store } from '@ngrx/store';
import { CdwDesignationEditComponent } from '@cdw-ae/cdw-designation-edit';
import {
  firstValueFrom,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { TailwindColors } from '../../../../../../../common/constants/tailwind-colors';
import { OrgUnitsDataService } from '../../org-units/org-units-data.service';
import { OrgUnitCollectionsDataService } from '../../ou-collections/ou-collections-data.service';
import { PermissionsDataService } from '../../permissions/permissions-data.service';
import { RolesDataService } from '../../roles/roles-data.service';
import { UserCollectionsDataService } from '../../user-collections/user-collections-data.service';
import { UsersDataService } from '../../users/users-data.service';
import { DesignationsDataService } from '../designations-data.service';

interface DesignationFormData {
  roleIds?: number[];
  orgUnitCollectionId?: number | null;
  orgUnitId?: string | null;
  userCollectionId?: number | null;
  userId?: number | null;
  type?: AccessManagementDesignationType;
}

@Component({
  selector: 'cdwae-designation-edit',
  templateUrl: './designation-edit.component.html',
  styleUrls: ['./designation-edit.component.scss'],
  imports: [
    CdwDesignationEditComponent,
    CommonModule,
    CdwDrawerModule,
    CdwDrawerContentModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    CdwDialogModule,
  ],
})
export class DesignationEditComponent implements OnInit {
  @ViewChild('designationEdit')
  public designationEditComponent!: CdwDesignationEditComponent;

  public currentOrgUnitCollectionOUs: AccessManagementOrgUnitCollectionOURecord[] =
    [];

  public currentDesignation: AccessManagementDesignationRecord | undefined;

  public userCollections: AccessManagementUserCollectionRecord[] = [];

  public orgUnitCollections: AccessManagementOrgUnitCollectionRecord[] = [];

  public roles: AccessManagementRoleRecord[] = [];

  public isLoading = false;

  public selectedDesignationId: number | 'new' = 'new';

  public userSelectOptions: UserRecord[] = [];

  public orgUnitSelectOptions: TreeNode[] = [];

  public drawerContent: DrawerContent[] = [];
  public drawerTitle = 'Summary';
  public drawerSubtitle = '';
  private currentDrawerItemType = '';
  private currentDrawerItemId: number | string = '';

  // Drawer styling to match other access management components
  public readonly gray50: string = TailwindColors.gray50;
  public readonly gray300: string = TailwindColors.gray300;
  public readonly tertiaryBlueCta: string = TailwindColors.tertiaryBlueCta;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private snackbar: MatSnackBar,
    private orgUnitsDataService: OrgUnitsDataService,
    private usersDataService: UsersDataService,
    private roleDataService: RolesDataService,
    private designationDataService: DesignationsDataService,
    private userCollectionsDataService: UserCollectionsDataService,
    private orgUnitCollectionsDataService: OrgUnitCollectionsDataService,
    private permissionsDataService: PermissionsDataService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private store: Store,
    private drawerNotifications: DrawerNotificationService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.isLoading = true;

    try {
      // Load all required data first
      this.orgUnitSelectOptions = await firstValueFrom(
        this.orgUnitsDataService.getOrgUnits()
      );

      this.userSelectOptions = (
        await firstValueFrom(this.usersDataService.getUsers())
      ).data;

      this.userCollections = (
        await firstValueFrom(
          this.userCollectionsDataService.getUserCollections()
        )
      ).data;

      this.roles = (await firstValueFrom(this.roleDataService.getRoles())).data;

      this.orgUnitCollections = (
        await firstValueFrom(
          this.orgUnitCollectionsDataService.getOrgUnitCollections()
        )
      ).data;

      // Now handle route parameters after data is loaded
      this.activatedRoute.paramMap
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((paramMap: ParamMap): void => {
          const idParam = paramMap.get('id');
          if (idParam && idParam !== 'new') {
            this.selectedDesignationId = parseInt(idParam, 10);

            this.designationDataService
              .getDesignationById(this.selectedDesignationId)
              .pipe(take(1))
              .subscribe((response) => {
                this.currentDesignation = response.data;
                this.isLoading = false;
              });
          } else {
            this.currentDesignation = undefined;
            this.selectedDesignationId = 'new';
            this.isLoading = false;
          }
        });
    } catch (_error) {
      // TODO: Implement proper error handling/logging
      this.isLoading = false;
    }
  }

  public async onSubmitEdit(event: DesignationFormData): Promise<void> {
    const roleIds = event.roleIds ?? [];

    if (roleIds.length === 0) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Please select at least one role for the designation.',
      });
      return;
    }

    // Convert OU path to UUID once upfront
    let resolvedOrgUnitId: string | null | undefined = event.orgUnitId;
    if (event.orgUnitId) {
      const orgUnitUuid = this.orgUnitsDataService.pathToUuidMap.get(event.orgUnitId);
      if (!orgUnitUuid) {
        this.snackbar.openFromComponent(ErrorSnackbarComponent, {
          data: `OU path not found: ${event.orgUnitId}`,
        });
        return;
      }
      resolvedOrgUnitId = orgUnitUuid;
    }

    // Check for duplicate designations per roleId
    let existingDesignationsResponse;
    try {
      existingDesignationsResponse = await firstValueFrom(
        this.designationDataService.getDesignations()
      );
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Error logging for API failure visibility
      console.error(
        'Failed to load existing designations for duplicate check:',
        error
      );
      existingDesignationsResponse = { data: [], itemTotal: 0 };
    }

    const existingDesignations = existingDesignationsResponse?.data ?? [];

    if (existingDesignations.length > 0) {
      const nullSafeEquals = (
        a: string | number | null | undefined,
        b: string | number | null | undefined
      ): boolean => {
        if (a == null && b == null) return true;
        return a === b;
      };

      for (const roleId of roleIds) {
        const isDuplicate = existingDesignations.some((designation) => {
          if (
            this.selectedDesignationId !== 'new' &&
            designation.id === this.selectedDesignationId
          ) {
            return false;
          }
          return (
            designation.roleId === roleId &&
            nullSafeEquals(designation.orgUnitCollectionId, event.orgUnitCollectionId) &&
            nullSafeEquals(designation.orgUnitId, resolvedOrgUnitId) &&
            nullSafeEquals(designation.userCollectionId, event.userCollectionId) &&
            nullSafeEquals(designation.userId, event.userId)
          );
        });

        if (isDuplicate) {
          const roleName = this.roles.find((r) => r.id === roleId)?.name || 'Unknown role';
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data: `A designation with role "${roleName}" already exists for this combination.`,
          });
          return;
        }
      }
    }

    // Build base payload shared across all roleIds
    const basePayload: {
      type: AccessManagementDesignationType;
      orgUnitCollectionId?: number;
      orgUnitId?: string;
      userCollectionId?: number;
      userId?: number;
    } = {
      type: event.type || AccessManagementDesignationType.USER_COLLECTION,
    };
    if (event.orgUnitCollectionId != null) {
      basePayload.orgUnitCollectionId = event.orgUnitCollectionId;
    }
    if (resolvedOrgUnitId != null) {
      basePayload.orgUnitId = resolvedOrgUnitId;
    }
    if (event.userCollectionId != null) {
      basePayload.userCollectionId = event.userCollectionId;
    }
    if (event.userId != null) {
      basePayload.userId = event.userId;
    }

    if (this.selectedDesignationId !== 'new') {
      // Edit mode: single atomic PUT carrying every roleId. The backend's
      // updateWithRoles wraps the existing-record rebind and any additional
      // role-row inserts in a TypeORM transaction — either all succeed or
      // none do, so we can't leave a partial write behind. Replaces the
      // previous two-step PUT-then-POST flow that had a partial-write
      // failure mode if the second call errored.
      this.designationDataService
        .updateDesignation({ ...basePayload, roleIds }, this.selectedDesignationId)
        .pipe(take(1))
        .subscribe(
          async (response: ApiResponse<AccessManagementDesignationRecord>) => {
            if (!response.success) {
              this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                data: 'An unexpected error occurred trying to save the designation',
              });
              return;
            }

            const message =
              roleIds.length > 1
                ? `Designation updated with ${roleIds.length} roles.`
                : 'Designation updated successfully.';
            this.snackbar.open(message, 'OK', { duration: 6000 });
            this.designationEditComponent.designationForm.markAsPristine();
            await this.router.navigate(['/administration/access-management']);
          }
        );
      return;
    }

    // Create mode: single request with all roleIds
    try {
      await firstValueFrom(
        this.designationDataService.addDesignation({ ...basePayload, roleIds })
      );

      const message =
        roleIds.length > 1
          ? `${roleIds.length} designations created successfully.`
          : 'Designation created successfully.';
      this.snackbar.open(message, 'OK', { duration: 6000 });
      this.designationEditComponent.designationForm.markAsPristine();
      await this.router.navigate(['/administration/access-management']);
    } catch {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'An unexpected error occurred trying to save the designation',
      });
    }
  }

  public onDeleteDesignation(): void {
    const deleteDialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      data: {
        title: 'Confirmation',
        message: 'Are you sure you want to delete this designation?',
        type: 'warning',
        layout: 'warning-box',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonColor: 'warn',
        hard: true,
      },
      width: '540px',
      maxWidth: '100%',
      disableClose: true,
      hasBackdrop: true,
    });

    deleteDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async (result: ConfirmationDialogResult | undefined) => {
        if (
          result?.confirmed &&
          this.selectedDesignationId &&
          this.selectedDesignationId !== 'new'
        ) {
          const observable =
            await this.designationDataService.deleteDesignation(
              this.selectedDesignationId
            );

          observable.pipe(take(1)).subscribe(async () => {
            this.snackbar.open('Designation deleted successfully.', 'OK', {
              duration: 6000,
            });
            await this.router.navigate(['/administration/access-management']);
          });
        }
      });
  }

  public async onCancelEdit(): Promise<void> {
    await this.router.navigate(['/administration/access-management']);
  }

  public onOpenDetailsDrawer(event: {
    type: string;
    id: number | string;
    name?: string;
  }): void {
    // Clear existing drawer content
    this.drawerContent = [];

    // Store current item type and ID for edit navigation
    this.currentDrawerItemType = event.type;
    this.currentDrawerItemId = event.id;

    if (event.type === 'role') {
      this.drawerTitle = 'Role summary';
      this.drawerSubtitle = event.name || '';

      // Fetch role details with permissions (matching access-management component)
      this.roleDataService
        .getRoleById(event.id as number)
        .pipe(take(1))
        .subscribe((response) => {
          if (response.success && response.data) {
            const { rolePermissions } = response.data;

            // Fetch all permissions to build the hierarchy
            this.permissionsDataService
              .getPermissions(AccessManagementPermissionAPIReturnFormats.MAP)
              .pipe(take(1))
              .subscribe((allPermissions: AccessManagementPermissionMap) => {
                this.drawerContent = this.buildRoleDrawerContent(
                  rolePermissions,
                  allPermissions
                );
              });
          }
        });
    } else if (event.type === 'userCollection') {
      this.drawerTitle = 'User collection summary';
      this.drawerSubtitle = event.name || '';

      // Fetch user collection details
      this.userCollectionsDataService
        .getUserCollectionById(event.id as number)
        .pipe(take(1))
        .subscribe((response) => {
          if (response.success && response.data) {
            const userCollection = response.data;
            const userNames =
              userCollection.userCollectionUsers
                ?.map(
                  (
                    userCollectionUser: AccessManagementUserCollectionUser
                  ): string => {
                    if (typeof userCollectionUser === 'string') {
                      return userCollectionUser;
                    }
                    return userCollectionUser?.user?.name || '';
                  }
                )
                .filter((name: string) => name) || [];

            // Create icons array with 'account_circle' for each user (matching access-management)
            const userIcons = userNames.map(() => 'account_circle');

            this.drawerContent = [
              {
                cellTitle: '',
                cellData: userNames,
                cellIcons: userIcons,
                displayStyle: 'bubble',
                showBorder: false,
                rowGap: '20px',
              },
            ];
          }
        });
    } else if (event.type === 'ouCollection') {
      this.drawerTitle = 'OU collection summary';
      this.drawerSubtitle = event.name || '';

      // Fetch OU collection details
      this.orgUnitCollectionsDataService
        .getOrgUnitCollectionById(event.id as number)
        .pipe(take(1))
        .subscribe((response) => {
          if (response.success && response.data) {
            const orgUnitCollection = response.data;
            const ouPaths =
              orgUnitCollection.ouCollectionOus
                ?.map(
                  (
                    ouCollectionOu: AccessManagementOrgUnitCollectionOU
                  ): string => {
                    if (typeof ouCollectionOu === 'string') {
                      return ouCollectionOu;
                    }
                    return ouCollectionOu?.orgUnit?.value || '';
                  }
                )
                .filter((path: string) => path) || [];

            this.drawerContent = [
              {
                cellTitle: '',
                cellData: ouPaths,
                titleFontFamily: 'Source Sans 3',
                titleFontSize: '18px',
                titleFontColor: TailwindColors.gray400,
                titleFontWeight: '700',
                dataFontSize: '14px',
                dataFontWeight: '400',
              },
            ];
          }
        });
    }

    // Open the drawer (dispatch both NgRx action and notification service for hybrid pattern)
    this.store.dispatch(openDrawer({ id: 'designationEditDrawer' }));
    this.drawerNotifications.notifyOpenDrawer('designationEditDrawer');
  }

  private buildRoleDrawerContent(
    rolePermissions: AccessManagementRolePermissionRecord[],
    allPermissions: AccessManagementPermissionMap
  ): DrawerContent[] {
    const drawerContent: DrawerContent[] = [];
    const onPermissionIds = new Set(
      rolePermissions.map((rp) => rp.permissionId)
    );
    const topLevelCategories = new Map<
      string,
      Map<
        string,
        { permissions: string[]; icons: string[]; iconColors: string[] }
      >
    >();

    // Build a set of IDs that are parents (have children)
    const parentIds = new Set<number>();
    Object.values(allPermissions).forEach(
      (perm: AccessManagementPermissionRecord & { path: string }) => {
        if (perm.parentId) {
          parentIds.add(perm.parentId);
        }
      }
    );

    // Group permissions by 3-level hierarchy
    Object.values(allPermissions).forEach(
      (permission: AccessManagementPermissionRecord & { path: string }) => {
        // Skip if this permission is a parent
        if (parentIds.has(permission.id)) {
          return;
        }

        // Only process leaf permissions
        if (!(permission.parentId || permission.parent)) {
          return;
        }

        const isOn = onPermissionIds.has(permission.id);
        const icon = isOn ? 'check' : 'close';
        const iconColor = isOn
          ? TailwindColors.chartGreen
          : TailwindColors.cdwRedCorpDark;

        // Get the parent (mid-level category)
        const parent =
          permission.parent ||
          (permission.parentId ? allPermissions[permission.parentId] : null);

        if (!parent) {
          return;
        }

        // Get the grandparent (top-level category)
        const grandparent =
          parent.parent ||
          (parent.parentId ? allPermissions[parent.parentId] : null);

        let topLevelName = 'General';
        let midLevelName = parent.name;

        if (grandparent) {
          topLevelName = grandparent.name;
        } else {
          topLevelName = parent.name;
          midLevelName = 'General';
        }

        // Create top-level category if it doesn't exist
        if (!topLevelCategories.has(topLevelName)) {
          topLevelCategories.set(topLevelName, new Map());
        }

        const midLevelCategories = topLevelCategories.get(topLevelName);
        if (!midLevelCategories) {
          return;
        }

        // Create mid-level category if it doesn't exist
        if (!midLevelCategories.has(midLevelName)) {
          midLevelCategories.set(midLevelName, {
            permissions: [],
            icons: [],
            iconColors: [],
          });
        }

        const category = midLevelCategories.get(midLevelName);
        if (!category) {
          return;
        }
        category.permissions.push(permission.name);
        category.icons.push(icon);
        category.iconColors.push(iconColor);
      }
    );

    // Build drawer content from hierarchy
    topLevelCategories.forEach((midLevelCategories, topLevelName) => {
      const hasPermissions = Array.from(midLevelCategories.values()).some(
        (cat) => cat.permissions.length > 0
      );

      if (!hasPermissions) {
        return;
      }

      // Add top-level header
      drawerContent.push({
        cellTitle: topLevelName.toUpperCase(),
        cellData: [],
        hideData: true,
        showBorder: false,
        titleFontFamily: 'Source Sans 3',
        titleFontSize: '16px',
        titleFontColor: TailwindColors.gray400,
        titleFontWeight: '700',
        titleLineHeight: '0',
        showBottomBorder: false,
        bottomBorderWidth: '1px',
        bottomBorderStyle: 'solid',
      });

      // Add each mid-level category with its permissions
      midLevelCategories.forEach((categoryData, midLevelName) => {
        if (categoryData.permissions.length === 0) {
          return;
        }

        if (midLevelName !== 'General') {
          drawerContent.push({
            cellTitle: midLevelName,
            cellData: categoryData.permissions,
            cellIcons: categoryData.icons,
            cellIconColors: categoryData.iconColors,
            showBorder: false,
            titleFontFamily: 'Source Sans 3',
            titleFontSize: '20px',
            titleFontColor: TailwindColors.gray200,
            titleFontWeight: '700',
            showBottomBorder: true,
            bottomBorderColor: TailwindColors.gray200,
            bottomBorderWidth: '1px',
            bottomBorderStyle: 'solid',
            dataLineHeight: '1.5',
          });
        } else {
          drawerContent.push({
            cellTitle: '',
            cellData: categoryData.permissions,
            cellIcons: categoryData.icons,
            cellIconColors: categoryData.iconColors,
            showBorder: false,
            dataFontSize: '16px',
            dataFontWeight: '400',
          });
        }
      });
    });

    return drawerContent;
  }

  public onDrawerEditClicked(): void {
    // Navigate to the appropriate edit page based on the current drawer item type
    switch (this.currentDrawerItemType) {
      case 'role':
        this.router.navigate([
          `administration/access-management/role/edit/${this.currentDrawerItemId}`,
        ]);
        break;
      case 'userCollection':
        this.router.navigate([
          `administration/access-management/user-collection/edit/${this.currentDrawerItemId}`,
        ]);
        break;
      case 'ouCollection':
        this.router.navigate([
          `administration/access-management/org-unit-collection/edit/${this.currentDrawerItemId}`,
        ]);
        break;
      default:
      // console.warn('Unknown drawer item type:', this.currentDrawerItemType);
    }
  }

  /**
   * Confirms navigation when user has unsaved changes
   * Shows a warning dialog to prevent accidental data loss
   * @returns Observable<boolean> - true if navigation allowed, false if blocked
   */
  confirmNavigationWithUnsavedChanges(): Observable<boolean> | boolean {
    // Check if form is dirty (has unsaved changes)
    if (!this.designationEditComponent?.designationForm?.dirty) {
      return true;
    }

    // Show confirmation dialog to warn user about data loss
    const dialogData: CdwConfirmationDialogData = {
      title: 'Confirmation',
      message:
        'You have unsaved changes. Are you sure you would like to continue?',
      type: 'warning',
      size: 'medium',
      layout: 'warning-box',
      fontFamily: 'Roboto',
      confirmText: 'Leave Page',
      cancelText: 'Stay on Page',
      confirmButtonColor: 'warn',
      showCancelButton: true,
      hard: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true, // Prevent Escape key and backdrop click from closing
    });

    // Return observable that resolves based on user's choice
    return dialogRef.afterClosed().pipe(
      map((result: { confirmed: boolean; dontShowAgain?: boolean }) => {
        return result?.confirmed ?? false;
      })
    );
  }
}
